import axios from "axios";
import { toastr } from "react-redux-toastr";
import { logout } from "./auth";

// The Ads pages: the campaigns the kiosks play (/api/ads/campaigns) and the report
// of their plays (/api/ads/report), the backend's utils/adCampaigns.js and
// utils/adPlays.js. Kept on the pages, not in the store: each call returns what the
// backend sent, or null when it failed and the error was shown.

// Shows the backend's { errors: [{ msg }] } and logs out on an expired token
const handleError = (err, dispatch) => {
	// No response: the network or the server is down
	if (!err.response) {
		toastr.error("Сервертэй холбогдож чадсангүй");
		return;
	}
	if (err.response.status === 401 && err.response.data.msg === "Token is not valid") {
		window.localStorage.clear("x-auth-token");
		toastr.error("Session expired");
		dispatch(logout());
	}
	const errors = err.response.data.errors;
	if (errors) {
		errors.forEach((error) => toastr.error(error.msg));
	}
};

const config = {
	headers: {
		"Content-Type": "application/json",
	},
};

// A backend without these routes answers with the admin's index.html and a 200
const isPage = (data) =>
	Boolean(data) && Array.isArray(data.campaigns) && Array.isArray(data.printers);
const isCampaign = (data) => Boolean(data) && typeof data._id === "string" && Array.isArray(data.creatives);

const call = async (dispatch, request, isRight, done) => {
	try {
		const res = await request();
		if (!isRight(res.data)) {
			toastr.error("Серверийн хариу буруу байна");
			return null;
		}
		if (done) toastr.success(done);
		return res.data;
	} catch (err) {
		handleError(err, dispatch);
		return null;
	}
};

//  Every campaign, and every kiosk with whether it has its list
export const getCampaigns = () => (dispatch) =>
	call(dispatch, () => axios.get("/api/ads/campaigns"), isPage);

//  Adds (no id) or saves a campaign's form:
//  { name, advertiser, startDay, endDay, weight, printers, enabled }
export const saveCampaign = (id, body, done = "Хадгалагдлаа") => (dispatch) =>
	call(
		dispatch,
		() =>
			id
				? axios.put(`/api/ads/campaigns/${id}`, JSON.stringify(body), config)
				: axios.post("/api/ads/campaigns", JSON.stringify(body), config),
		isCampaign,
		done,
	);

//  Removes a campaign that has never played
export const deleteCampaign = (id) => (dispatch) =>
	call(dispatch, () => axios.delete(`/api/ads/campaigns/${id}`), (data) => Boolean(data && data._id), "Устгагдлаа");

//  Uploads a video or image to a campaign, with what the browser read of it
//  ({ durationSec, width, height, lengthSec }). onProgress gets 0-1.
export const uploadCreative = (campaignId, file, fields, onProgress) => (dispatch) => {
	const form = new FormData();
	form.append("file", file);
	Object.entries(fields).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== "") form.append(key, String(value));
	});
	return call(
		dispatch,
		() =>
			axios.post(`/api/ads/campaigns/${campaignId}/creatives`, form, {
				onUploadProgress: (event) => event.total && onProgress(event.loaded / event.total),
			}),
		isCampaign,
		"Бүтээл нэмэгдлээ",
	);
};

//  Turns a creative on or off, and sets how long an image is shown
export const saveCreative = (campaignId, creativeId, body) => (dispatch) =>
	call(
		dispatch,
		() => axios.put(`/api/ads/campaigns/${campaignId}/creatives/${creativeId}`, JSON.stringify(body), config),
		isCampaign,
	);

//  Removes a creative that has never played
export const deleteCreative = (campaignId, creativeId) => (dispatch) =>
	call(
		dispatch,
		() => axios.delete(`/api/ads/campaigns/${campaignId}/creatives/${creativeId}`),
		isCampaign,
		"Устгагдлаа",
	);

// Only the newest report request may fill the page, so a slow answer for an earlier
// range does not replace the one picked after it
let latestReport = 0;

//  The plays of the days from and to ("YYYY-MM-DD"), both included, by day, kiosk,
//  creative, size and page. Returns { report }, { error } or { stale: true }.
export const getAdReport = (from, to) => async (dispatch) => {
	const request = ++latestReport;
	try {
		const res = await axios.get("/api/ads/report", { params: { from, to } });
		if (request !== latestReport) return { stale: true };
		if (!res.data || !Array.isArray(res.data.rows)) return { error: "Серверийн хариу буруу байна" };
		return { report: res.data };
	} catch (err) {
		if (request !== latestReport) return { stale: true };
		const errors = err.response && err.response.data && err.response.data.errors;
		if (errors && errors.length) return { error: errors[0].msg };
		handleError(err, dispatch);
		return { error: "Тайланг ачаалж чадсангүй" };
	}
};
